import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Brain, FileText, TrendingUp, UserCheck, Target, RefreshCw, UserPlus,
} from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
  fetchCoachId,
  fetchCoachStats,
  fetchCoachAssessmentsOverTime,
  fetchCoachCharacteristicAverages,
  fetchCoachHarmonicAverages,
  fetchCoachRecentCompletions,
  fetchCoachRespondentBreakdown,
  type CoachStats,
  type CoachMonthlyAssessment,
  type CharacteristicAverage,
  type HarmonicAverage,
  type CoachRecentCompletion,
  type RespondentBreakdown,
} from '../../lib/coach-dashboard';
import { CoachAssessmentsChart } from '../../components/coach/CoachAssessmentsChart';
import { CoachCharacteristicsOverview } from '../../components/coach/CoachCharacteristicsOverview';
import { CoachHarmonicOverview } from '../../components/coach/CoachHarmonicOverview';
import { CoachRecentCompletions } from '../../components/coach/CoachRecentCompletions';
import { CoachRespondentBreakdown } from '../../components/coach/CoachRespondentBreakdown';
import { InviteRespondentModal, ShareLinkCard } from '../../components/coach/InviteRespondentModal';

export function CoachDashboardTR() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [respondentLimit, setRespondentLimit] = useState(50);
  const [assessmentCode, setAssessmentCode] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [stats, setStats] = useState<CoachStats>({
    totalRespondents: 0,
    completedAssessments: 0,
    pendingAssessments: 0,
    completionRate: 0,
    aiAnalysesGenerated: 0,
    retakesCompleted: 0,
  });
  const [assessmentsOverTime, setAssessmentsOverTime] = useState<CoachMonthlyAssessment[]>([]);
  const [characteristicAverages, setCharacteristicAverages] = useState<CharacteristicAverage[]>([]);
  const [harmonicAverages, setHarmonicAverages] = useState<HarmonicAverage[]>([]);
  const [recentCompletions, setRecentCompletions] = useState<CoachRecentCompletion[]>([]);
  const [respondentBreakdown, setRespondentBreakdown] = useState<RespondentBreakdown>({
    completed: 0, pending: 0, withAiAnalysis: 0, withRetakes: 0,
  });

  useEffect(() => {
    if (!user?.id) return;
    loadDashboard();
  }, [user?.id]);

  const loadDashboard = async () => {
    if (!user?.id) return;

    try {
      const { data: coachRecord } = await supabase
        .from('coaches')
        .select('tier, max_respondents, assessment_code')
        .eq('user_id', user.id)
        .maybeSingle();

      setRespondentLimit(coachRecord?.max_respondents || 50);
      setAssessmentCode(coachRecord?.assessment_code || null);

      const id = await fetchCoachId(user.id);

      if (id) {
        const [
          statsData, assessmentsData, characteristicsData,
          harmonicsData, completionsData, breakdownData,
        ] = await Promise.all([
          fetchCoachStats(id),
          fetchCoachAssessmentsOverTime(id),
          fetchCoachCharacteristicAverages(id),
          fetchCoachHarmonicAverages(id),
          fetchCoachRecentCompletions(id),
          fetchCoachRespondentBreakdown(id),
        ]);

        setStats(statsData);
        setAssessmentsOverTime(assessmentsData);
        setCharacteristicAverages(characteristicsData);
        setHarmonicAverages(harmonicsData);
        setRecentCompletions(completionsData);
        setRespondentBreakdown(breakdownData);
      }
    } catch (err) {
      console.error('Error loading coach data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Koc paneli yukleniyor..." />;
  }

  const statCards = [
    { label: 'Toplam Katilimci', value: stats.totalRespondents, icon: Users, color: 'bg-success-40' },
    { label: 'Tamamlanan', value: stats.completedAssessments, icon: FileText, color: 'bg-teal-50' },
    { label: 'Bekleyen', value: stats.pendingAssessments, icon: UserCheck, color: 'bg-orange-40' },
    { label: 'Tamamlanma Orani', value: `${stats.completionRate}%`, icon: Target, color: 'bg-brand-40' },
    { label: 'YZ Analizleri', value: stats.aiAnalysesGenerated, icon: Brain, color: 'bg-teal-50' },
    { label: 'Tekrar Alma', value: stats.retakesCompleted, icon: RefreshCw, color: 'bg-success-50' },
  ];

  return (
    <div className="min-h-screen freud-bg py-8 px-6">
      <AnimatePresence>
        {showInviteModal && assessmentCode && (
          <InviteRespondentModal
            assessmentCode={assessmentCode}
            coachName={user?.user_metadata?.full_name || user?.email || ''}
            onClose={() => setShowInviteModal(false)}
            locale="tr"
          />
        )}
      </AnimatePresence>
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="freud-heading-2xl text-chocolate-900">Kontrol Paneli</h1>
            <p className="freud-text-base text-chocolate-500">Kocluk genel bakisiniz</p>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="freud-btn-primary flex items-center gap-2 flex-shrink-0"
          >
            <UserPlus className="w-4 h-4" />
            Davet Et
          </button>
        </div>

        {assessmentCode && (
          <ShareLinkCard assessmentCode={assessmentCode} locale="tr" />
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.06 }}
              className="freud-card-elevated p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 ${stat.color} rounded-xl shadow-soft`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
                <TrendingUp className="w-4 h-4 text-gray-30" />
              </div>
              <div className="text-text-2xl text-gray-80 mb-0.5">{stat.value}</div>
              <div className="text-text-xs text-gray-40">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <CoachAssessmentsChart data={assessmentsOverTime} locale="tr" />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>
            <CoachRecentCompletions completions={recentCompletions} locale="tr" />
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <motion.div className="lg:col-span-2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <CoachCharacteristicsOverview data={characteristicAverages} locale="tr" />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}>
            <CoachRespondentBreakdown data={respondentBreakdown} locale="tr" />
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
          <CoachHarmonicOverview data={harmonicAverages} locale="tr" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
          className="bg-gradient-to-r from-sage-600 to-sage-700 rounded-2xl p-6 text-white shadow-soft-lg"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="freud-heading-base text-white mb-1">Katilimci Kapasitesi</h3>
              <p className="freud-text-sm text-sage-100">
                {stats.totalRespondents} / {respondentLimit} katilimci kullanildi
              </p>
            </div>
            <div className="text-right">
              <div className="freud-heading-2xl text-white">
                {Math.round((stats.totalRespondents / (respondentLimit || 1)) * 100)}%
              </div>
              <p className="freud-text-sm text-sage-100">kapasite kullanildi</p>
            </div>
          </div>
          <div className="mt-4 h-2 bg-sage-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all"
              style={{ width: `${(stats.totalRespondents / (respondentLimit || 1)) * 100}%` }}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
